import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GripVertical, Trash2 } from 'lucide-react';
import type { Topic, TopicSubtopic } from '../../types/curriculumTopic';
import { formatTotalDuration } from '../../utils/durationFormat';
import { reorderList } from '../../utils/reorder';
import { ROUTES } from '../../utils/createRoutes';
import { TopicDurationField } from '../shared/TopicDurationField';
import {
  allTopicsHaveDuration,
  saveCurriculumPathForTimeline,
} from '../../utils/curriculumPathStorage';

interface ActiveLearningPathProps {
  topics: Topic[];
  onRemove: (id: string) => void;
  onReorder: (topics: Topic[]) => void;
  onDurationChange: (id: string, durationMinutes: number) => void;
  onSubtopicDurationChange?: (
    topicId: string,
    subId: string,
    minutes: number,
  ) => void;
  onSubtopicRemove?: (topicId: string, subId: string) => void;
  onSubtopicTitleChange?: (topicId: string, subId: string, title: string) => void;
  onSubtopicReorder?: (topicId: string, subtopics: TopicSubtopic[]) => void;
  courseId?: string;
  onContinue?: () => void;
  /** Course plan seed: no add/remove/reorder; durations only. */
  structureLocked?: boolean;
}

export function ActiveLearningPath({
  topics,
  onRemove,
  onReorder,
  onDurationChange,
  onSubtopicDurationChange,
  onSubtopicRemove,
  onSubtopicTitleChange,
  onSubtopicReorder,
  courseId,
  onContinue,
  structureLocked = false,
}: ActiveLearningPathProps) {
  const navigate = useNavigate();

  // Topic-level drag
  const [topicDrag, setTopicDrag] = useState<number | null>(null);
  const [topicOver, setTopicOver] = useState<number | null>(null);

  // Subtopic-level drag (scoped to a topic)
  const [subDrag, setSubDrag] = useState<{ topicId: string; idx: number } | null>(null);
  const [subOver, setSubOver] = useState<{ topicId: string; idx: number } | null>(null);

  const grandTotal = topics.reduce((sum, t) => {
    const selectedSubs = t.subtopics?.filter((s) => s.selected) ?? [];
    if (selectedSubs.length > 0) {
      return sum + selectedSubs.reduce((s, sub) => s + (sub.durationMinutes || 0), 0);
    }
    return sum + (t.durationMinutes || 0);
  }, 0);
  const canAddToTimeline = allTopicsHaveDuration(topics);

  const clearTopicDrag = () => { setTopicDrag(null); setTopicOver(null); };
  const clearSubDrag = () => { setSubDrag(null); setSubOver(null); };

  const handleTopicDrop = (toIndex: number) => {
    if (topicDrag === null || topicDrag === toIndex) { clearTopicDrag(); return; }
    onReorder(reorderList(topics, topicDrag, toIndex));
    clearTopicDrag();
  };

  const handleSubDrop = useCallback((topicId: string, toIdx: number) => {
    if (!subDrag || subDrag.topicId !== topicId || subDrag.idx === toIdx) {
      clearSubDrag();
      return;
    }
    const topic = topics.find((t) => t.id === topicId);
    if (!topic?.subtopics) { clearSubDrag(); return; }
    const selected = topic.subtopics.filter((s) => s.selected);
    const reordered = reorderList(selected, subDrag.idx, toIdx);
    // Rebuild full subtopics: replace selected ones with reordered, keep unselected
    const unselected = topic.subtopics.filter((s) => !s.selected);
    onSubtopicReorder?.(topicId, [...reordered, ...unselected]);
    clearSubDrag();
  }, [subDrag, topics, onSubtopicReorder]);

  const handleContinue = () => {
    if (!canAddToTimeline) return;
    if (onContinue) { onContinue(); return; }
    saveCurriculumPathForTimeline(topics);
    navigate(ROUTES.topicPlanner, { state: { fromCurriculum: true } });
  };

  return (
    <div className="codelive-active-path">
      <div className="card h-100" style={{ borderRadius: 12 }}>
        <div
          className="card-header"
          style={{ background: '#fff', borderBottom: '1px solid #e2e8f0' }}
        >
          <h6 className="mb-0 fw-bold">Active Learning Path</h6>
          {!structureLocked && (
            <small className="text-muted">Drag to reorder topics or sub-topics</small>
          )}
          <div className="path-meta d-flex justify-content-between small">
            <span>
              Total: <strong>{formatTotalDuration(grandTotal)}</strong>
            </span>
            <span>{topics.length} topic{topics.length === 1 ? '' : 's'}</span>
          </div>
        </div>

        <div className="card-body d-flex flex-column gap-3">
          {topics.map((topic, index) => {
            const isDragging = topicDrag === index;
            const isOver = topicOver === index && topicDrag !== index;
            const selectedSubs = topic.subtopics?.filter((s) => s.selected) ?? [];
            const topicTotal = selectedSubs.length > 0
              ? selectedSubs.reduce((sum, s) => sum + s.durationMinutes, 0)
              : topic.durationMinutes || 0;

            return (
              <div
                key={topic.id}
                className={isDragging ? 'opacity-50' : ''}
                style={{
                  borderRadius: 10,
                  border: isOver ? '2px solid #2563eb' : '1px solid #e2e8f0',
                  background: '#fff',
                }}
              >
                {/* Topic header — draggable */}
                <div
                  draggable={!structureLocked}
                  onDragStart={(e) => {
                    if (structureLocked) return;
                    e.stopPropagation();
                    setTopicDrag(index);
                    setTopicOver(null);
                  }}
                  onDragEnd={clearTopicDrag}
                  onDragOver={(e) => {
                    if (structureLocked) return;
                    e.preventDefault();
                    e.dataTransfer.dropEffect = 'move';
                    setTopicOver(index);
                  }}
                  onDrop={(e) => {
                    if (structureLocked) return;
                    e.preventDefault();
                    handleTopicDrop(index);
                  }}
                  className="d-flex align-items-center gap-2 px-3 py-2"
                  style={{
                    background: '#f8fafc',
                    borderRadius: selectedSubs.length > 0 ? '10px 10px 0 0' : '10px',
                    borderBottom: selectedSubs.length > 0 ? '1px solid #e2e8f0' : 'none',
                    cursor: structureLocked ? 'default' : 'grab',
                  }}
                >
                  {!structureLocked && (
                    <GripVertical className="text-muted flex-shrink-0" size={16} />
                  )}
                  <span className="fw-semibold small flex-grow-1 text-truncate">
                    {topic.title}
                  </span>
                  {selectedSubs.length === 0 && (
                    <div className="flex-shrink-0" style={{ maxWidth: 220 }}>
                      <TopicDurationField
                        topicId={topic.id}
                        topicTitle={topic.title}
                        minutes={topic.durationMinutes}
                        courseId={courseId}
                        onChange={(m) => onDurationChange(topic.id, m)}
                      />
                    </div>
                  )}
                  {selectedSubs.length > 0 && topicTotal > 0 && (
                    <span className="text-muted small flex-shrink-0 fw-semibold">
                      {topicTotal} min
                    </span>
                  )}
                  {!structureLocked && (
                  <button
                    type="button"
                    draggable={false}
                    onClick={() => onRemove(topic.id)}
                    className="btn btn-sm p-1 text-danger flex-shrink-0"
                    aria-label={`Remove ${topic.title}`}
                  >
                    <Trash2 size={14} />
                  </button>
                  )}
                </div>

                {/* Subtopics — each individually draggable within this topic */}
                {selectedSubs.length > 0 && (
                  <div className="px-3 py-2 d-flex flex-column gap-2">
                    {selectedSubs.map((sub, subIdx) => {
                      const isSubDragging = subDrag?.topicId === topic.id && subDrag.idx === subIdx;
                      const isSubOver = subOver?.topicId === topic.id && subOver.idx === subIdx
                        && subDrag?.topicId === topic.id && subDrag.idx !== subIdx;

                      return (
                        <div
                          key={sub.id}
                          draggable={!structureLocked}
                          onDragStart={(e) => {
                            if (structureLocked) return;
                            e.stopPropagation();
                            setSubDrag({ topicId: topic.id, idx: subIdx });
                            setSubOver(null);
                          }}
                          onDragEnd={clearSubDrag}
                          onDragOver={(e) => {
                            if (structureLocked) return;
                            e.preventDefault();
                            e.stopPropagation();
                            e.dataTransfer.dropEffect = 'move';
                            setSubOver({ topicId: topic.id, idx: subIdx });
                          }}
                          onDrop={(e) => {
                            if (structureLocked) return;
                            e.preventDefault();
                            e.stopPropagation();
                            handleSubDrop(topic.id, subIdx);
                          }}
                          className={`border px-3 py-2 ${isSubDragging ? 'opacity-50' : ''}`}
                          style={{
                            marginLeft: 8,
                            borderRadius: 8,
                            background: '#f9fafb',
                            borderColor: isSubOver ? '#2563eb' : '#e2e8f0',
                            borderWidth: isSubOver ? 2 : 1,
                            cursor: structureLocked ? 'default' : 'grab',
                          }}
                        >
                          <div className="d-flex align-items-center gap-2">
                            {!structureLocked && (
                              <GripVertical className="text-muted flex-shrink-0" size={12} />
                            )}
                            <span className="small fw-medium flex-grow-1" style={{ color: '#334155' }}>
                              {sub.title || 'Subtopic'}
                            </span>
                            {!structureLocked && (
                            <button
                              type="button"
                              draggable={false}
                              onClick={() => onSubtopicRemove?.(topic.id, sub.id)}
                              className="btn btn-sm p-0 text-danger flex-shrink-0"
                              aria-label={`Remove ${sub.title}`}
                            >
                              <Trash2 size={13} />
                            </button>
                            )}
                          </div>
                          <div className="mt-1" style={{ paddingLeft: structureLocked ? 0 : 20 }}>
                            <TopicDurationField
                              topicId={sub.id}
                              topicTitle={sub.title}
                              minutes={sub.durationMinutes > 0 ? sub.durationMinutes : null}
                              courseId={courseId}
                              onChange={(m) =>
                                onSubtopicDurationChange?.(topic.id, sub.id, m)
                              }
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}

          {topics.length === 0 && (
            <p className="text-muted small mb-0">No topics added yet.</p>
          )}
        </div>

        {!structureLocked && (
        <div
          className="card-footer d-flex align-items-center justify-content-between"
          style={{ background: '#f8fafc' }}
        >
          <span className="fw-bold">Grand Total Duration</span>
          <span className="fw-bold" style={{ color: '#2563eb', fontSize: 18 }}>
            {formatTotalDuration(grandTotal)}
          </span>
        </div>
        )}

        <div className="path-continue">
          <button
            type="button"
            disabled={!canAddToTimeline}
            onClick={handleContinue}
            className={`btn w-100 d-flex align-items-center justify-content-center gap-2 ${
              canAddToTimeline ? 'btn-primary' : 'btn-secondary'
            }`}
            style={{ borderRadius: 8, opacity: canAddToTimeline ? 1 : 0.65 }}
          >
            {structureLocked ? 'Continue' : 'Add to Timeline'}
          </button>
          {!structureLocked && !canAddToTimeline && topics.length > 0 && (
            <p className="text-warning small text-center mb-0 mt-2">
              Add at least one subtopic to every topic and set a duration for each
              before adding to the timeline.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
